/**
 * SocialLinks — uniform row of brand social outlets.
 *
 * Used in both the Footer (every page) and a Home-page sticker section.
 * Pinterest is a custom inline SVG because lucide-react doesn't ship one;
 * the others use lucide for visual consistency with the rest of the icon set.
 *
 * SEO note: also referenced by the schema.org JSON-LD `sameAs` array in
 * index.html. Keep URLs in sync if these ever change.
 */
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

export const SOCIAL_LINKS = [
    {
        name: "Facebook",
        href: "https://www.facebook.com/profile.php?id=61553120296003",
        Icon: Facebook,
    },
    {
        name: "Instagram",
        href: "https://www.instagram.com/my.eco.tour/",
        Icon: Instagram,
    },
    {
        name: "LinkedIn",
        href: "https://www.linkedin.com/company/whispering-earth-my-ecotour1/",
        Icon: Linkedin,
    },
    {
        name: "Pinterest",
        href: "https://www.pinterest.com/myecotour/",
        Icon: PinterestIcon,
    },
    {
        name: "YouTube",
        href: "https://www.youtube.com/@whisperingearth",
        Icon: Youtube,
    },
];

function PinterestIcon({ size = 18, strokeWidth = 1.75, className = "" }) {
    // Lucide-styled stroked Pinterest "P" mark. Kept inline so the lucide-react
    // tree-shake doesn't blow up — we just need one extra glyph, not a
    // second icon library.
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="10" />
            <path d="M9 19c.5-2 .8-3.6 1.2-5.5" />
            <path d="M8 12.5c-.3-1 0-3 2-3 1.5 0 2.5 1 2.5 2.5 0 2-1.4 3.5-3 3.5-1.2 0-2-.8-2-2" />
        </svg>
    );
}

export default function SocialLinks({
    tone = "light",
    showLabels = false,
    className = "",
    testIdPrefix = "social",
}) {
    // Tone variants — `light` for dark backgrounds (footer/hero), `dark` for
    // light backgrounds (home section card).
    const base =
        tone === "dark"
            ? "text-primary hover:text-accent border-primary/20 hover:border-accent"
            : "text-primary-foreground/85 hover:text-accent border-primary-foreground/20 hover:border-accent";

    return (
        <ul className={`flex flex-wrap items-center gap-3 ${className}`} data-testid={`${testIdPrefix}-row`}>
            {SOCIAL_LINKS.map(({ name, href, Icon }) => (
                <li key={name}>
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer me"
                        aria-label={`MyEcoTour on ${name}`}
                        title={name}
                        data-testid={`${testIdPrefix}-${name.toLowerCase()}`}
                        className={`inline-flex items-center gap-2 px-3 py-2 border transition-colors ${base}`}
                    >
                        <Icon size={16} strokeWidth={1.75} />
                        {showLabels && <span className="text-xs tracking-[0.18em] uppercase">{name}</span>}
                    </a>
                </li>
            ))}
        </ul>
    );
}
